"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Sparkles,
  ArrowRight,
  Mic,
  Crown,
  Target,
  CheckSquare,
  ClipboardCheck,
  Eye,
  Award,
  Layers,
  Network,
  Compass,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { StepperBlock } from "@/lib/blocks/stepper-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

export default function OperationalExcellencePage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="Glossary · Operational Excellence"
        overlineClassName="text-amber-gold"
        title={
          <>
            The Engineered System That Produces <span className="text-gradient-premium">Verified Outcomes</span>
          </>
        }
        subtitle="The systematic pursuit of flawless execution within an organization — the discipline of designing, building, and running systems so precisely that exceptional outcomes become the default rather than the exception."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Glossary", href: "/glossary/" }, { label: "Operational Excellence" }]}
        badge="Four components · Clinical origins"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
            <Sparkles className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Featured Snippet Definition</span>
          </div>
          <h2 className="text-display-md mb-5">What operational excellence is.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            <span className="font-semibold text-foreground">Operational excellence</span> is an operational philosophy and organizational discipline focused on the continuous improvement of processes, systems, and execution to deliver consistent, measurable, and superior outcomes. Rather than treating excellence as an abstract value or aspirational goal, operational excellence treats it as an engineered system — one built on protocols, checklists, rigorous reviews, and unflinching outcome accountability.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            In practice, operational excellence means that every function within an organization operates according to designed standards that eliminate variance, reduce failure modes, and produce results that can be verified, replicated, and scaled.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The concept differs from operational efficiency, which optimizes for cost and speed. Operational excellence optimizes for <em>outcome quality under pressure</em> — the ability to deliver correct results when the margin for error is zero and the stakes are irreversible.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Award className="size-3.5 text-red" />
            <span className="text-overline text-red">The Core Principle</span>
          </div>
          <h2 className="text-display-md mb-4">Excellence is a system, not a value.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Most organizations declare excellence as a core value. They print it on walls and include it in mission statements. This is the fundamental error. A value is a belief. A system is a mechanism. Beliefs do not produce outcomes — mechanisms do.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${ACCENT.red.bg} to-transparent border ${ACCENT.red.border}`}>
            <h3 className={`text-heading-md mb-2 text-red`}>The Aspiration Trap</h3>
            <p className="text-body-sm text-muted-foreground leading-relaxed">Operational excellence begins with a radical premise: that excellence cannot be achieved through aspiration, motivation, or cultural reinforcement. It can only be achieved through design. The protocols must be specified. The checklists must be built. The reviews must be scheduled and enforced.</p>
          </div>
          <div className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${ACCENT.emerald.bg} to-transparent border ${ACCENT.emerald.border}`}>
            <h3 className={`text-heading-md mb-2 text-emerald`}>The Engineered Alternative</h3>
            <p className="text-body-sm text-muted-foreground leading-relaxed">This principle is what separates organizations that achieve operational excellence from those that merely pursue it. The difference is not effort or commitment — it is architecture. The former builds systems that produce excellence as output. The latter hopes that excellence will emerge as a byproduct of good intentions.</p>
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Four Components</span>
          </div>
          <h2 className="text-display-md mb-4">Protocols, checklists, reviews, and outcome accountability.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Operational excellence rests on four interdependent components. Remove any one, and the system collapses into conventional management.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { icon: CheckSquare, title: "Protocols", body: "Designed procedures that govern every operation. They specify not just what must be done, but the exact sequence, the required inputs, the validation steps, and the escalation paths when conditions deviate from specification.", accent: "red" },
            { icon: ClipboardCheck, title: "Checklists", body: "Verification instruments that ensure nothing is omitted. Borrowed directly from clinical medicine — where a surgical checklist prevents fatal omissions — checklists serve as the final barrier between intent and execution.", accent: "purple" },
            { icon: Eye, title: "Reviews", body: "Structured evaluations that assess execution against standards. They are not casual check-ins. They are formal assessments with defined criteria, documented findings, and mandatory corrective actions.", accent: "cyan" },
            { icon: Award, title: "Outcome Accountability", body: "The principle that every operation is owned by a named individual who bears responsibility for its result. There is no diffusion of responsibility. The operator who owns the outcome owns it completely.", accent: "emerald" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg`}>
                    <p.icon className="size-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Network className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Related Terms</span>
          </div>
          <h2 className="text-display-md mb-4">The conceptual map.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { term: "Strategic Sovereignty", href: "/glossary/strategic-sovereignty/", desc: "The integrated discipline operational excellence supports.", accent: "red" },
            { term: "Leadership as Diagnosis", href: "/glossary/leadership-as-diagnosis/", desc: "The clinical method that produces operational excellence.", accent: "denim" },
            { term: "Sovereign Operations Architecture", href: "/glossary/sovereign-operations-architecture/", desc: "The methodology that operationalizes the system.", accent: "emerald" },
            { term: "Crisis Transformation", href: "/glossary/crisis-transformation/", desc: "The application of operational excellence under pressure.", accent: "coral" },
            { term: "Penta P's Framework", href: "/glossary/penta-ps-framework/", desc: "The five-domain model the system operates within.", accent: "cyan" },
            { term: "70% Operating Model", href: "/glossary/70-percent-operating-model/", desc: "The team architecture that produces the talent base.", accent: "emerald" },
          ].map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={t.href} className="block h-full group">
                  <div className={`relative glass-card rounded-2xl p-5 h-full bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                    <h3 className={`text-heading-sm mb-2 ${a.text}`}>{t.term}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed mb-3">{t.desc}</p>
                    <span className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                      Read <ArrowRight className="size-3" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <CheckSquare className="size-3.5 text-red" />
            <span className="text-overline text-red">Excellence Steps</span>
          </div>
          <h2 className="text-display-md mb-4">The five stages of operational excellence.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Operational excellence is built through five sequential stages — from diagnostic assessment through continuous refinement. Each stage produces deliverables that feed the next.
          </p>
        </div>
        <StepperBlock
          steps={[
            { num: "01", title: "Diagnostic Assessment", body: "Identify the specific gaps between current performance and required outcomes. The organizational equivalent of differential diagnosis. Without this stage, the architecture addresses symptoms rather than root causes.", accent: "red" },
            { num: "02", title: "Architecture Design", body: "Design the protocols, checklists, review cycles, and accountability structures that will govern operations. The architecture is specific to the organization's context — no two architectures are identical.", accent: "purple" },
            { num: "03", title: "Deployment & Training", body: "Embed the architecture into daily operations through training, enforcement, and leadership modeling. The most senior person in the organization must model the behavior. The architecture decays within 18-24 months without leadership commitment.", accent: "cyan" },
            { num: "04", title: "Continuous Review", body: "Honest, rigorous assessment of what is working and what is not. The review stage converts experience into institutional knowledge. Without the review stage, the architecture becomes folklore rather than operating reality.", accent: "emerald" },
            { num: "05", title: "Refinement & Evolution", body: "Continuous improvement based on verified outcomes. The architecture gets better with every cycle. The compounding effect is what makes the operational excellence system a sovereign-grade operating system rather than a project.", accent: "amber-gold" },
            { num: "06", title: "Institutional Embedding", body: "The architecture becomes structural rather than dependent on individuals. Leadership transitions become operational adjustments rather than crisis events. The institutional knowledge persists across generational change.", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Compass className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Usage Examples</span>
          </div>
          <h2 className="text-display-md mb-4">Operational excellence in practice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four examples of operational excellence applied across the most demanding environments. Each example shows the engineered system in action.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "Surgical Team Parallel", body: "A surgical team that follows a pre-operative checklist, executes a standardized protocol, conducts a post-operative review, and achieves consistent patient outcomes regardless of which specific team members are present. The outcome is produced by the system, not by individual talent.", accent: "red" },
            { title: "Sovereign-Scale Operations", body: "The integrated platform ecosystem operates across eighteen countries. Operational uptime of 99.9999%. Zero security incidents. Coordination success rate of 95%. The outcomes are produced by the engineered system.", accent: "purple" },
            { title: "Crisis Response Engine", body: "500+ pre-built response playbooks activated within minutes of crisis detection. The Golden Hour Principle compressed. The operational excellence substrate makes sovereign-grade crisis response possible.", accent: "cyan" },
            { title: "Governance Transformation", body: "National eGovernance deployment serving up to 120 million citizens. The architecture supports the multi-year program through every stage, from diagnostic assessment through institutional embedding.", accent: "emerald" },
          ]}
          columns={2}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <BookOpen className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Common Misconceptions</span>
          </div>
          <h2 className="text-display-md mb-4">What operational excellence is not.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six persistent misconceptions about operational excellence. The misconceptions are common because the term is used loosely in management literature.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "Is operational excellence the same as operational efficiency?", a: "No. Operational efficiency optimizes for cost and speed. Operational excellence optimizes for outcome quality under pressure. The distinction matters: efficiency without excellence produces fast, cheap failures." },
            { q: "Is operational excellence a set of tools?", a: "No. Tools are enablers. Operational excellence is an engineered system that uses tools, protocols, checklists, reviews, and accountability structures. The system produces excellent outcomes. The tools support the system." },
            { q: "Can operational excellence be achieved through aspiration?", a: "No. Excellence cannot be achieved through motivation, culture, or aspiration. The protocols must be specified. The checklists must be built. The reviews must be scheduled and enforced. Aspiration produces effort. Systems produce outcomes." },
            { q: "Is operational excellence a project?", a: "No. Operational excellence is a continuous discipline. Projects have end dates. Operational excellence is built, maintained, and continuously refined throughout the organization's existence. The most common failure mode is treating it as a project with a completion date." },
            { q: "Does operational excellence require perfection?", a: "No. Operational excellence requires systems that produce excellent outcomes most of the time, with mechanisms to detect and correct deviations. The system handles the exceptions. The architecture prevents most exceptions. The review stage learns from the remainder." },
            { q: "Can operational excellence be applied to creative work?", a: "Yes. Creative work benefits from the same architectural discipline as any other operational domain. The protocols specify the conditions, the inputs, and the validation steps. The creative capacity operates within the architecture. Excellence is the output." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Target className="size-10 text-amber-gold mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage the operating system.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Operational excellence is not a value statement. It is an operating architecture — and the engagements that produce it are governed by the five-step methodology.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/glossary/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                Glossary Hub
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Glossary Hub", description: "Ten terms defining sovereign-scale operations.", href: "/glossary/", icon: BookOpen, accent: "cyan" },
          { title: "FAQ: Operational Excellence", description: "The engineered system in detail.", href: "/faq/operational-excellence/", icon: Target, accent: "amber-gold" },
          { title: "About Dr. Jyoti Kush", description: "The full story of the operational architect.", href: "/about/", icon: Crown, accent: "red" },
          { title: "The Operator's Notebook", description: "Biweekly insights on how excellent things run.", href: "/the-operators-notebook/", icon: Compass, accent: "denim" },
        ]}
      />
    </>
  );
}
